import { Fragment } from "react";
import { useTranslation } from "react-i18next";
import { Exam } from "src/types";

const HIDDEN_CODES = new Set(["SZ"]);

const KIND_LABELS: Record<string, string> = {
    L: "Auslage / Material",
    M: "Medikament",
    P: "Porto",
    G: "Gutachten",
    H: "Honorar",
    E: "Entschädigung",
    B: "Begr.",
    K: "Keine Berechnung",
};

// Same six KIND_LABELS kinds as FLAT_FEE_CATEGORY_LABELS below, but keyed by the raw
// materialLines "kind" letter rather than the rendered label — gates the label prefix on a
// material line attached to a REAL exam (e.g. "Medikament: Tetagam" under a real GOÄ Ziffer),
// the other place the same category label shows up besides a standalone flat-fee exam's own
// description. "B" (Begr.) and "K" are deliberately excluded — annotation/note kinds, not
// cost categories, so hideCategoryLabels never touches them.
const FLAT_FEE_CATEGORY_KINDS = new Set(["L", "M", "P", "G", "H", "E"]);

const fmt = (n: number) =>
    n.toLocaleString("de-DE", {
        style: "currency",
        currency: "EUR",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

// Helper to strip technical PAD prefixes
const stripPrefixes = (text: string) =>
    (text || "")
        .replace(/^\s*(Text|Begründungstext|Begründung|Begr|Berg)\.?\s*:\s*/i, "")
        .trim();

// Zuschlag codes carry an internal "<Section>-" disambiguation prefix (e.g. "B-A"
// for Zuschlag A) that isn't real GOÄ notation — never show it to the user.
const displayCode = (code: string) => (code || "").replace(/^[A-Z]-(?=[A-Z])/, "");

// A bare (or SK-prefixed) G/H/L/M/P/E as an exam's own code is a flat-fee flag, not a real
// GOÄ Ziffer — see the SK-prefix normalization in backend's pad/createExams.ts and
// examController.ts. All six letters are equally just "no real GOÄ Ziffer" flags in
// practice — real MEDISTAR exports use G/H for plain vaccine products exactly like L/M/P/E,
// not specifically for Gutachten/Honorar — so every one gets the same treatment: showing it
// as a position number would collide visually with the real Zuschlag "B-G"/"B-H" notation
// once displayCode() strips its section prefix, so it's blanked here the same way
// generateInvoicePDF/sections.ts blanks it on the printed PDF; and none of them show a
// Steigerungsfaktor, since a flat fee isn't an Untersuchung with a multiplier.
const FLAT_FEE_CODES = new Set(["G", "H", "L", "M", "P", "E", "SKG", "SKH", "SKL", "SKM", "SKP", "SKE"]);
const isFlatFeeLine = (exam: Exam) =>
    exam.description === "Sachkosten/Auslagen" ||
    FLAT_FEE_CODES.has((exam.code || "").trim().toUpperCase());

// pad/createExams.ts and examController.ts prefix every flat-fee exam's own description with
// its per-letter category label (matching KIND_LABELS above) — e.g. "Gutachten: Gardasil 9
// -Impfung". Repeating the same category on every row of a consecutive run (e.g. 20
// standalone MEDISTAR vaccine lines, mostly the same letter) reads as noisy, so it's shown
// once per run and hidden (not removed — same reserved-width trick as the materialLines
// sub-rows below) on the rest, tabbing them under where the first row's real name starts.
// The run resets whenever the category itself changes, not just when a non-flat-fee line
// interrupts it.
const FLAT_FEE_CATEGORY_LABELS = new Set(["Auslage / Material", "Medikament", "Porto", "Gutachten", "Honorar", "Entschädigung"]);

/** Extracts the leading "<Category>: " prefix pad/createExams.ts/examController.ts add to a
 *  flat-fee exam's description, or null if this isn't such a line (a real GOÄ position, or
 *  legacy "Sachkosten/Auslagen" data that predates per-letter categorization). */
function extractCategoryPrefix(exam: Exam): string | null {
    const text = exam.description || "";
    const colonIdx = text.indexOf(": ");
    if (colonIdx === -1) return null;
    const candidate = text.slice(0, colonIdx);
    return FLAT_FEE_CATEGORY_LABELS.has(candidate) ? candidate : null;
}

export function ExamTable({
    exams,
    vatRate = 0,
    editable = false,
    hideCategoryLabels = false,
    onGroupClick,
}: {
    exams: Exam[];
    vatRate?: number;
    editable?: boolean;
    hideCategoryLabels?: boolean;
    onGroupClick?: (date: string, groupExams: Exam[]) => void;
}) {
    const { t } = useTranslation();
    // Exams arrive in whatever order the API happens to return them, not the PAD file's
    // true order. positionsnr is the file's own running position counter, so sort by
    // that when available; fall back to date-only for exams that predate this field.
    const visible = exams
        .filter((e) => !HIDDEN_CODES.has((e.code ?? "").toUpperCase()))
        .sort((a, b) => {
            if (a.positionsnr != null && b.positionsnr != null) {
                return a.positionsnr - b.positionsnr;
            }
            return new Date(a.date ?? 0).getTime() - new Date(b.date ?? 0).getTime();
        });

    // Per-line VAT: each exam uses its own vatRate override (e.g. a PADneXt position-level
    // <mwstsatz>) when present, falling back to the invoice's own vatRate otherwise —
    // mirrors computeInvoiceVat in backend/src/services/examTotals.ts. Reduces to the exact
    // old flat "net * vatRate / 100" whenever every line ends up on one effective rate,
    // which is still true for the overwhelming majority of invoices.
    let net = 0;
    const baseByRate = new Map<number, number>();
    for (const exam of visible) {
        const betrag =
            exam.materialCosts ||
            (exam.quantity ?? 1) * (exam.price ?? 0) * (exam.factor ?? 1);
        const lineBase =
            betrag + (exam.materialLines ?? []).reduce((s, ml) => s + (ml.cost || 0), 0);
        net += lineBase;
        const rate = exam.vatRate ?? vatRate ?? 0;
        baseByRate.set(rate, (baseByRate.get(rate) ?? 0) + lineBase);
    }
    const rateGroups = [...baseByRate.entries()]
        .filter(([rate]) => rate > 0)
        .map(([rate, base]) => ({ rate, base: +base.toFixed(2), vat: +((base * rate) / 100).toFixed(2) }))
        .sort((a, b) => b.rate - a.rate);
    const vatAmount = +rateGroups.reduce((s, r) => s + r.vat, 0).toFixed(2);
    const gross = +(net + vatAmount).toFixed(2);
    const exemptBase = +(net - rateGroups.reduce((s, r) => s + r.base, 0)).toFixed(2);
    // "The whole invoice has VAT" — every line shares the same single nonzero rate, nothing
    // exempt mixed in. Only then is a Net/VAT/Total breakdown meaningful; a mix of taxed and
    // exempt lines (or several different rates) would make a "VAT (7%)" row on the FULL net
    // misleading — it'd read as 7% of everything, when only some lines actually are. That
    // case is instead explained line-by-line (see the per-row note below) and just totals up.
    const uniform = rateGroups.length === 1 && Math.abs(exemptBase) < 0.005;

    return (
        <table className="exam-table">
            <thead>
                <tr>
                    <th>{t("invoicePage.examDate")}</th>
                    <th className="exam-table__num">
                        {t("invoicePage.examQty")}
                    </th>
                    <th className="exam-table__num">
                        {t("invoicePage.examPosition")}
                    </th>
                    <th>{t("invoicePage.examDescription")}</th>
                    <th className="exam-table__num">
                        {t("invoicePage.examValue")}
                    </th>
                    <th className="exam-table__num">
                        {t("invoicePage.examFactor")}
                    </th>
                    <th className="exam-table__num">
                        {t("invoicePage.examAmount")}
                    </th>
                </tr>
            </thead>
            <tbody>
                {visible.map((exam, i) => {
                    const betrag =
                        exam.materialCosts ||
                        (exam.quantity ?? 1) *
                            (exam.price ?? 0) *
                            (exam.factor ?? 1);
                    const hasValues =
                        (exam.price ?? 0) !== 0 ||
                        (exam.materialCosts ?? 0) !== 0;

                    // One row per material line, each with its own quantity/unit price/
                    // cost — consistent with the PDF and with every other row in this
                    // table (one row = one verifiable cost). Previously, consecutive
                    // same-kind entries (e.g. two drugs billed under one service) were
                    // merged into a single row with a summed cost, hiding the individual
                    // amounts from anyone trying to verify the total.
                    const lines = exam.materialLines ?? [];

                    // The line amount above (e.g. 4,02 €) is always the NET GOÄ fee, same as
                    // every other row — VAT is never folded into it. When this line carries
                    // its own vatRate, show what that adds in euros right alongside the rate,
                    // so it's clear the 4,02 € isn't already VAT-inclusive.
                    const lineVatBase = betrag + lines.reduce((s, ml) => s + (ml.cost || 0), 0);
                    const lineVatAmount = (exam.vatRate ?? 0) > 0 ? +(lineVatBase * (exam.vatRate as number) / 100).toFixed(2) : 0;

                    const prevDate = i > 0 ? visible[i - 1].date : undefined;
                    const showDate = !prevDate || exam.date !== prevDate;
                    const rawCategory = extractCategoryPrefix(exam);
                    // "Hide Kennzeichen" setting: never show the category label, but still
                    // strip it from the visible description — the stored exam.description
                    // itself is untouched, only what's displayed here.
                    const category = hideCategoryLabels ? null : rawCategory;
                    const prevCategory = hideCategoryLabels || i === 0 ? null : extractCategoryPrefix(visible[i - 1]);
                    const showCategoryLabel = category !== null && category !== prevCategory;
                    const plainDescription = rawCategory !== null && hideCategoryLabels
                        ? (exam.description ?? "").slice(rawCategory.length + 2)
                        : exam.description;

                    return (
                        <Fragment key={`${exam.id}-${i}`}>
                            <tr className={betrag < 0 ? "exam-table__row--negative" : undefined}>
                                <td>
                                    {showDate && exam.date ? (
                                        editable && onGroupClick ? (
                                            <button
                                                type="button"
                                                className="exam-table__date--clickable"
                                                onClick={() =>
                                                    onGroupClick(
                                                        exam.date!,
                                                        visible.filter(
                                                            (e) =>
                                                                e.date ===
                                                                exam.date,
                                                        ),
                                                    )
                                                }
                                                title={t(
                                                    "invoicePage.editServicesHint",
                                                )}
                                            >
                                                {new Date(
                                                    exam.date,
                                                ).toLocaleDateString(
                                                    "de-DE",
                                                    {
                                                        day: "2-digit",
                                                        month: "2-digit",
                                                        year: "2-digit",
                                                    },
                                                )}
                                            </button>
                                        ) : (
                                            new Date(
                                                exam.date,
                                            ).toLocaleDateString("de-DE", {
                                                day: "2-digit",
                                                month: "2-digit",
                                                year: "2-digit",
                                            })
                                        )
                                    ) : (
                                        ""
                                    )}
                                </td>
                                <td className="exam-table__num">
                                    {exam.quantity}
                                </td>
                                <td className="exam-table__num">{isFlatFeeLine(exam) ? "" : displayCode(exam.code)}</td>
                                <td>
                                    {category !== null ? (
                                        // Same color as every other exam row (inherited, no override) —
                                        // italic is what marks this as a flat-fee/Kennzeichen-labeled line,
                                        // matching .exam-table__material-label's identical treatment for
                                        // the same category prefix on a material sub-line.
                                        <span className="exam-table__category-label">
                                            <span style={showCategoryLabel ? undefined : { visibility: "hidden" }}>
                                                {category}:{" "}
                                            </span>
                                            {(exam.description ?? "").slice(category.length + 2)}
                                        </span>
                                    ) : (
                                        plainDescription
                                    )}
                                    {/* Per-line VAT override note — only shown when this line explicitly
                                        carries its own vatRate (e.g. a PADneXt position-level override
                                        that differs from the rest of the invoice). Silent for the vast
                                        majority of lines, which just use the invoice-level rate. */}
                                    {exam.vatRate != null && (
                                        <span className="exam-table__vat-note" title={t("invoicePage.lineVatOverrideHint")}>
                                            {exam.vatRate > 0
                                                ? t("invoicePage.lineVatRateAmount", { rate: exam.vatRate, amount: fmt(lineVatAmount) })
                                                : t("invoicePage.lineVatExempt")}
                                        </span>
                                    )}
                                </td>
                                <td className="exam-table__num">
                                    {hasValues ? fmt(exam.price ?? 0) : ""}
                                </td>
                                <td className="exam-table__num">
                                    {hasValues && !isFlatFeeLine(exam)
                                        ? (exam.factor ?? 1).toLocaleString(
                                              "de-DE",
                                              {
                                                  minimumFractionDigits: 3,
                                                  maximumFractionDigits: 3,
                                              },
                                          )
                                        : ""}
                                </td>
                                <td className="exam-table__num exam-table__num--bold">
                                    {hasValues ? fmt(betrag) : ""}
                                </td>
                            </tr>

                            {/* Annotations (T-Kind logic) — shown right after the main line and
                                BEFORE its material lines, so a Begründung stays visually attached
                                to the Ziffer it explains instead of reading like the last item in
                                a (possibly long) Auslagen list. */}
                            {exam.annotations &&
                                exam.annotations.length > 0 && (
                                    <tr className="exam-table__annotation-row">
                                        <td colSpan={3}></td>
                                        <td
                                            colSpan={1}
                                            className="exam-table__annotation"
                                        >
                                            (
                                            {exam.annotations
                                                .map((a) => stripPrefixes(a))
                                                .join(", ")}
                                            )
                                        </td>
                                    </tr>
                                )}

                            {/* Manually-entered Begründung */}
                            {exam.justification && (
                                <tr className="exam-table__justification-row">
                                    <td colSpan={3}></td>
                                    <td
                                        colSpan={1}
                                        className="exam-table__justification"
                                    >
                                        {t("invoicePage.justification")}:{" "}
                                        {exam.justification}
                                    </td>
                                </tr>
                            )}

                            {/* Render each material line on its own row */}
                            {(() => {
                                const shownKinds = new Set<string>();
                                return lines.map((ml, idx) => {
                                    const kind = (ml.kind || "").toUpperCase();
                                    const isT = kind === "T";
                                    const text = stripPrefixes(ml.label || "");
                                    // "Hide Kennzeichen" setting: for the six cost-category kinds, drop
                                    // the label entirely (not just visually hide it) — there's then
                                    // nothing to align later same-kind rows under, so they start flush
                                    // left like an unlabeled line, same as the top-level exam handling.
                                    const categoryHidden = hideCategoryLabels && FLAT_FEE_CATEGORY_KINDS.has(kind);
                                    const kindLabel = KIND_LABELS[kind] || kind;
                                    // Label prefix only on first occurrence per kind per position row —
                                    // matches generateInvoicePDF/sections.ts. But unlike the PDF (which
                                    // measures and reserves a per-kind pixel width so later same-kind
                                    // lines still line up under the first one's text), this was rendering
                                    // "prefix: text" as one flat string with nothing holding that space
                                    // open once the prefix is dropped — so an unlabeled line snapped back
                                    // to the left edge instead of staying aligned. Rendering the label
                                    // span every time, only toggling visibility, reserves the exact same
                                    // width (same text, same font) so alignment holds without needing to
                                    // measure anything.
                                    const showLabel = !isT && !shownKinds.has(kind);
                                    if (!isT) shownKinds.add(kind);

                                    return (
                                        <tr
                                            key={`${exam.id}-ml-${idx}`}
                                            className={`exam-table__material-row${(ml.cost ?? 0) < 0 ? " exam-table__row--negative" : ""}`}
                                        >
                                            <td></td>
                                            <td className="exam-table__num">
                                                {ml.quantity ?? ""}
                                            </td>
                                            <td></td>
                                            {/* Italic marks a real Kennzeichen (L/M/P/G/H/E) line as flat-fee/
                                                material — a T entry is a parenthesized comment/note, not a
                                                Kennzeichen, and stays upright like any other note. */}
                                            <td className={`exam-table__material-label${isT ? "" : " exam-table__material-label--kennzeichen"}`}>
                                                {isT ? (
                                                    `(${text})`
                                                ) : categoryHidden ? (
                                                    text
                                                ) : (
                                                    <>
                                                        <span
                                                            className="exam-table__material-kind"
                                                            style={showLabel ? undefined : { visibility: "hidden" }}
                                                        >
                                                            {kindLabel}:{" "}
                                                        </span>
                                                        {text}
                                                    </>
                                                )}
                                            </td>
                                            <td className="exam-table__num">
                                                {ml.unitPrice != null ? fmt(ml.unitPrice) : ""}
                                            </td>
                                            <td></td>
                                            <td className="exam-table__num exam-table__num--bold">
                                                {ml.cost ? fmt(ml.cost) : ""}
                                            </td>
                                        </tr>
                                    );
                                });
                            })()}
                        </Fragment>
                    );
                })}
            </tbody>
            <tfoot>
                {uniform ? (
                    <>
                        <tr className="exam-table__sum-row exam-table__sum-row--sub">
                            <td colSpan={6}>{t("invoicePage.net")}</td>
                            <td className="exam-table__num exam-table__num--bold">
                                {fmt(net)}
                            </td>
                        </tr>
                        <tr className="exam-table__sum-row exam-table__sum-row--sub">
                            <td colSpan={6}>
                                {t("invoicePage.vat", { rate: rateGroups[0].rate })}
                            </td>
                            <td className="exam-table__num exam-table__num--bold">
                                {fmt(vatAmount)}
                            </td>
                        </tr>
                        <tr className="exam-table__sum-row">
                            <td colSpan={6}>{t("invoicePage.total")}</td>
                            <td className="exam-table__num exam-table__num--bold">
                                {fmt(gross)}
                            </td>
                        </tr>
                    </>
                ) : (
                    // Fully exempt, or a genuine mix of taxed/exempt lines (or several
                    // different rates) — the per-line notes above already explain which
                    // lines were taxed and at what rate, so the total just adds up
                    // (gross === net when nothing is taxed).
                    <tr className="exam-table__sum-row">
                        <td colSpan={6}>{t("invoicePage.total")}</td>
                        <td className="exam-table__num exam-table__num--bold">
                            {fmt(gross)}
                        </td>
                    </tr>
                )}
            </tfoot>
        </table>
    );
}
